import type { GameCondition, GameRuleAction, GameScene, GameSceneChoice, GameSceneGraph } from "./game-definition.js";
import { compileGameDefinition } from "./game-definition.js";
import { normalizeGameDefinition, type NormalizedGameDefinition } from "./normalize-game-definition.js";
import type { RoqeetGameDocument } from "./roqeet-game-document.js";
import type { GameModule } from "../ports/game-module.js";

export interface RoqeetRuntimePreview {
  document: RoqeetGameDocument;
  definition: NormalizedGameDefinition;
  module: GameModule;
  state: Record<string, unknown>;
  currentSceneId: string;
}

export interface CreateRuntimePreviewOptions {
  sceneId?: string;
  setup?: Record<string, string>;
  state?: Record<string, unknown>;
}

export interface ApplyChoiceOptions {
  sceneId?: string;
  state?: Record<string, unknown>;
}

export interface ApplyChoiceResult extends RoqeetRuntimePreview {
  choice: GameSceneChoice;
  scene: GameScene | undefined;
}

export function createRuntimePreview(
  document: RoqeetGameDocument,
  options: CreateRuntimePreviewOptions = {},
): RoqeetRuntimePreview {
  const definition = normalizeGameDefinition(document);
  const module = compileGameDefinition(definition);
  const state = options.state ? cloneState(options.state) : (module.initialState?.(options.setup) ?? {});
  const currentSceneId = options.sceneId ?? definition.sceneGraph?.startSceneId ?? firstScene(definition.sceneGraph)?.id ?? "";
  return { document, definition, module, state, currentSceneId };
}

export function getScene(
  input: RoqeetGameDocument | RoqeetRuntimePreview,
  sceneId?: string,
): GameScene | undefined {
  const sceneGraph = toSceneGraph(input);
  const id = sceneId ?? (isRuntimePreview(input) ? input.currentSceneId : sceneGraph?.startSceneId);
  if (!id) return firstScene(sceneGraph);
  return sceneGraph?.scenes.find((scene) => scene.id === id);
}

export function getChoices(
  input: RoqeetGameDocument | RoqeetRuntimePreview,
  sceneId?: string,
  state?: Record<string, unknown>,
): GameSceneChoice[] {
  const effectiveState = state ?? (isRuntimePreview(input) ? input.state : {});
  return (getScene(input, sceneId)?.choices ?? []).filter((choice) => conditionsPass(choice.when, effectiveState));
}

export function applyChoice(
  input: RoqeetGameDocument | RoqeetRuntimePreview,
  choiceRef: string | GameSceneChoice,
  options: ApplyChoiceOptions = {},
): ApplyChoiceResult {
  const preview = isRuntimePreview(input)
    ? input
    : createRuntimePreview(input, { sceneId: options.sceneId, state: options.state });
  const sceneId = options.sceneId ?? preview.currentSceneId;
  const state = cloneState(options.state ?? preview.state);
  const choice = resolveChoice(getChoices(preview, sceneId, state), choiceRef);
  if (!choice) {
    throw new Error(`Choice "${String(choiceRef)}" is not available in scene "${sceneId}".`);
  }
  const nextState = applyChoiceEffects(state, choice.effects);
  const currentSceneId = choice.to ?? sceneId;
  return {
    document: preview.document,
    definition: preview.definition,
    module: preview.module,
    state: nextState,
    currentSceneId,
    choice,
    scene: getScene(preview, currentSceneId),
  };
}

export function buildPrompt(
  input: RoqeetGameDocument | RoqeetRuntimePreview,
  options: { sceneId?: string; state?: Record<string, unknown> } = {},
): string {
  const preview = isRuntimePreview(input)
    ? input
    : createRuntimePreview(input, { sceneId: options.sceneId, state: options.state });
  const state = options.state ?? preview.state;
  const scene = getScene(preview, options.sceneId ?? preview.currentSceneId);
  const base = preview.module.protocolInstructions?.(state) ?? preview.definition.instructions;
  const parts = [base.trim()];
  if (scene?.content.trim()) {
    const heading = scene.title ? `Current scene (${scene.id}): ${scene.title}` : `Current scene (${scene.id})`;
    parts.push(`${heading}\n${scene.content.trim()}`);
  }
  return parts.filter(Boolean).join("\n\n");
}

function resolveChoice(choices: GameSceneChoice[], choiceRef: string | GameSceneChoice): GameSceneChoice | undefined {
  if (typeof choiceRef !== "string") return choiceRef;
  return choices.find((choice) => choice.id === choiceRef || choice.label === choiceRef || choice.to === choiceRef);
}

function applyChoiceEffects(
  state: Record<string, unknown>,
  effects: GameRuleAction[] | undefined,
): Record<string, unknown> {
  if (!effects?.length) return state;
  const next = { ...state };
  for (const effect of effects) {
    switch (effect.op) {
      case "set":
        next[effect.key] = effect.value;
        break;
      case "inc":
        next[effect.key] = toNumber(next[effect.key]) + toNumber(effect.value);
        break;
      case "dec":
        next[effect.key] = toNumber(next[effect.key]) - toNumber(effect.value);
        break;
    }
  }
  return next;
}

function conditionsPass(
  conditions: GameCondition[] | undefined,
  state: Record<string, unknown>,
): boolean {
  return (conditions ?? []).every((condition) => conditionPasses(condition, state));
}

function conditionPasses(condition: GameCondition, state: Record<string, unknown>): boolean {
  const current = state[condition.key];
  switch (condition.op) {
    case "truthy":
      return !!current;
    case "falsy":
      return !current;
    case "eq":
      return current === condition.value;
    case "neq":
      return current !== condition.value;
    case "lte":
      return toNumber(current) <= toNumber(condition.value);
    case "gte":
      return toNumber(current) >= toNumber(condition.value);
    case "lt":
      return toNumber(current) < toNumber(condition.value);
    case "gt":
      return toNumber(current) > toNumber(condition.value);
  }
}

function firstScene(sceneGraph: GameSceneGraph | undefined): GameScene | undefined {
  return sceneGraph?.scenes[0];
}

function toSceneGraph(input: RoqeetGameDocument | RoqeetRuntimePreview): GameSceneGraph | undefined {
  return isRuntimePreview(input) ? input.definition.sceneGraph : normalizeGameDefinition(input).sceneGraph;
}

function isRuntimePreview(input: RoqeetGameDocument | RoqeetRuntimePreview): input is RoqeetRuntimePreview {
  return "document" in input && "module" in input && "currentSceneId" in input;
}

function cloneState(state: Record<string, unknown>): Record<string, unknown> {
  return { ...state };
}

function toNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}
